import { create } from 'zustand';
import { favoritesService } from '../services/favoritesService';
import { BackendLocation } from '../types/location';
import { ChargingLocation } from '../types';
import { transformLocations } from '../utils/transformers';
import { handleApiError, captureError } from '../lib/errorHandler';

interface FavoritesState {
  favorites: ChargingLocation[];
  rawFavorites: BackendLocation[];
  isLoading: boolean;
  favoriteIds: Set<string>;

  fetchFavorites: (latitude?: number, longitude?: number) => Promise<void>;
  toggleFavorite: (locationId: string) => Promise<void>;
  isFavorite: (locationId: string) => boolean;
  reset: () => void;
}

export const useFavoritesStore = create<FavoritesState>((set, get) => ({
  favorites: [],
  rawFavorites: [],
  isLoading: false,
  favoriteIds: new Set(),

  fetchFavorites: async (latitude?: number, longitude?: number) => {
    set({ isLoading: true });
    try {
      const raw = await favoritesService.getFavorites(latitude, longitude);
      const favorites = transformLocations(raw);
      set({
        favorites,
        rawFavorites: raw,
        favoriteIds: new Set(raw.map((l) => l.id)),
        isLoading: false,
      });
    } catch (error) {
      captureError(error, 'FavoritesStore.fetchFavorites');
      set({ isLoading: false });
    }
  },

  toggleFavorite: async (locationId: string) => {
    const { favoriteIds } = get();
    const wasFavorite = favoriteIds.has(locationId);

    // Optimistic update
    const next = new Set(favoriteIds);
    if (wasFavorite) {
      next.delete(locationId);
    } else {
      next.add(locationId);
    }
    set({ favoriteIds: next });

    try {
      if (wasFavorite) {
        await favoritesService.removeFavorite(locationId);
      } else {
        await favoritesService.addFavorite(locationId);
      }
      await get().fetchFavorites();
    } catch (error) {
      captureError(error, 'FavoritesStore.toggleFavorite');
      handleApiError(error);
      // Revert on failure
      set({ favoriteIds: new Set(favoriteIds) });
    }
  },

  isFavorite: (locationId: string) => {
    return get().favoriteIds.has(locationId);
  },

  reset: () => {
    set({ favorites: [], rawFavorites: [], isLoading: false, favoriteIds: new Set() });
  },
}));
